import {
  Droplets, Syringe, Activity, Baby, HeartPlus, Pill, Zap, Thermometer, FlaskConical,
  ClipboardPen, ListChecks, Users,
} from 'lucide-react';

/*
 * Ben Taub Obstetric Anesthesiology rotation. Built from the residency Blackboard
 * course (orientation slides Feb 2022, logistics and clinical primers Sept 2021,
 * resident responsibilities, Duramorph/ERAC refresher, protocols) — see
 * docs/blackboard-ben-taub-ob.md. Items marked confirm await the site director.
 *
 * In-house extensions are *3 + four digits; the one full number given on the slides
 * (LD attending *39554 = 713-873-9554) is the basis for the tap-to-call numbers.
 */

const BB_DOC = (id) => `https://bcm.blackboard.com/ultra/courses/_129_1/document/${id}?view=content&state=view`;
const ext = (n) => `7138${n.slice(1)}`; // *3xxxx -> 713-873-xxxx is the pattern on the slides; applied to all *3 numbers

export const benTaubOb = {
  accent: '#00697f',
  notesKey: 'notes:ben-taub:ob',

  toolSections: [
    { title: 'Clinical tools', tools: [
      { id: 'pph', title: 'Postpartum Hemorrhage', subtitle: 'Staged checklist and uterotonics', icon: Droplets, action: 'guide:pph', featured: true },
      { id: 'neuraxial', title: 'Neuraxial Dosing', subtitle: 'Labor, cesarean, BTL, cerclage', icon: Syringe, action: 'guide:neuraxial' },
      { id: 'cesarean', title: 'Cesarean', subtitle: 'Urgency classes and OR setup', icon: Baby, action: 'guide:cesarean' },
      { id: 'phenylephrine', title: 'Phenylephrine Infusion', subtitle: 'Spinal hypotension', icon: Activity, action: 'guide:phenylephrine' },
      { id: 'erac', title: 'Enhanced Recovery', subtitle: 'ERAC pathway', icon: HeartPlus, action: 'guide:erac' },
      { id: 'duramorph', title: 'Duramorph Orders', subtitle: 'Neuraxial morphine and follow-up', icon: Pill, action: 'guide:duramorph' },
      { id: 'eclampsia', title: 'Eclamptic Seizure', subtitle: 'Magnesium in the OR', icon: Zap, action: 'guide:eclampsia' },
      { id: 'sepsis', title: 'Sepsis', subtitle: 'Screen, confirm, antibiotics', icon: Thermometer, action: 'guide:sepsis' },
      { id: 'drugs', title: 'OB Drugs', subtitle: 'Uterotonics, tocolytics, look-alikes', icon: FlaskConical, action: 'guide:drugs' },
    ] },
    { title: 'Logistics', tools: [
      { id: 'epic', title: 'Epic', subtitle: 'Labor epidural charting, cassettes, closing', icon: ClipboardPen, action: 'guide:epic' },
      { id: 'daily', title: 'Daily Checks & Schedule', subtitle: 'Room checks, signout, call codes', icon: ListChecks, action: 'guide:daily' },
      { id: 'roles', title: 'Team Roles', subtitle: 'What each year owns', icon: Users, action: 'guide:roles' },
    ] },
  ],

  contacts: {
    storageKey: 'ben-taub-ob-favorites',
    groups: [
      { id: 'anesthesia', title: 'OB anesthesia', open: true, items: [
        { label: 'LD anesthesia attending', phone: '7138739554', note: 'Call if late or unable to come in' },
        { label: 'OB anesthesia office', phone: ext('32900') },
        { label: 'Facilitator resident / CRNA', phone: ext('39555') },
        { label: 'OB anesthesia junior', phones: [{ label: '*39556', phone: ext('39556') }, { label: '*37937', phone: ext('37937') }] },
        { label: 'Faculty call room', phone: ext('32839') },
        { label: 'OB anesthesia lounge (LRC)', phone: ext('32905') },
        { label: 'Anesthesia tech', phones: [{ label: '*39020', phone: ext('39020') }, { label: '*39021', phone: ext('39021') }, { label: '*39023', phone: ext('39023') }] },
        { label: 'CRNA', phone: ext('39427') },
      ] },
      { id: 'ld', title: 'Labor and delivery', open: true, items: [
        { label: 'LD attending', phone: ext('39511') },
        { label: 'Gyn hospitalist', phone: ext('39512') },
        { label: 'LD charge nurse', phone: ext('39490') },
        { label: 'LD nursing front desk', phone: ext('32810') },
        { label: 'OB intake (OBI)', phone: ext('32243') },
        { label: 'Midwife', phone: ext('39500') },
        { label: 'OB resident lounge', phone: ext('38774') },
      ] },
      { id: 'support', title: 'Lab, blood bank, pharmacy', items: [
        { label: '4th floor stat lab', phone: ext('32732') },
        { label: 'Blood bank', phone: ext('33250') },
        { label: '4th floor pharmacy', phone: ext('34400') },
      ] },
      { id: 'accreta', title: 'Pavilion accreta team', items: [
        { label: 'Dr Fox', phone: '4095998948', note: 'Backup for refractory hemorrhage' },
        { label: 'Dr Belfort', phone: '8322892972', note: 'Backup for refractory hemorrhage' },
      ] },
    ],
  },

  locations: {
    maps: [],
    elevators: {},
    groups: [
      { id: 'ld', title: 'Labor and delivery, 3rd floor', items: [
        { name: 'Labor and delivery unit', floor: '3rd floor', note: 'LDRs, OB ORs and the OB intake (OBI)' },
        { name: 'OB anesthesia lounge (LRC)', floor: '3rd floor', note: 'Signout rounds on weekends and holidays. Anesthesia charging station for team-phone batteries' },
        { name: 'OG 10', floor: '3rd floor', note: 'Fiberoptic bronchoscope, C-MAC with #3, #4 and D-blade, GlideScope' },
        { name: 'Anesthesia workroom', floor: '3rd floor', where: 'McGrath in the drawer, blades in the locked room', code: '1964' },
        { name: 'Anesthesia supply room', floor: '3rd floor', code: '1964' },
        { name: 'Biohazard room', floor: '3rd floor', note: 'Empty epidural cassettes go in the black bucket. Remove patient labels', code: '2060' },
      ] },
      { id: 'codes', title: 'Cart and cabinet codes', items: [
        { name: 'LDR cabinets', floor: 'Each LDR', note: 'Keep locked, CMS requirement', code: '2244', codeLabel: 'Cabinet' },
        { name: 'Epidural cart', floor: 'L&D', note: 'Keep locked, CMS requirement', code: '123', codeLabel: 'Cart' },
        { name: 'Epidural pump', floor: 'L&D', code: '997', codeLabel: 'Pump' },
        { name: 'Pink carts in the ORs', floor: 'OB ORs', note: 'Syringes', code: '1234', codeLabel: 'Cart' },
      ] },
      { id: 'fourth', title: '4th floor', items: [
        { name: 'Stat lab', floor: '4th floor' },
        { name: 'Pharmacy', floor: '4th floor' },
      ] },
    ],
  },

  documents: {
    storageKey: 'ben-taub-ob-pinned-docs',
    groups: [
      { id: 'orientation', title: 'Orientation', items: [
        { title: 'OB orientation presentation', subtitle: 'Video', url: 'https://www.youtube.com/watch?v=B1rEnidRLWM' },
        { title: 'L&D unit walkthrough', subtitle: 'Video', url: 'https://www.youtube.com/watch?v=lIG5djsQr94' },
        { title: 'Trainee primer: clinical', subtitle: 'Blackboard, sign-in required', url: BB_DOC('_268622_1') },
        { title: 'Trainee primer: logistics', subtitle: 'Blackboard, sign-in required', url: BB_DOC('_268622_1') },
      ] },
      { id: 'references', title: 'References', items: [
        { title: 'Stanford Emergency Manual', subtitle: 'Keep on your phone', url: 'https://emergencymanual.stanford.edu/' },
        { title: 'OB protocols on Blackboard', subtitle: 'Full PDFs, sign-in required', url: BB_DOC('_257828_1') },
        { title: 'OB anesthesia literature', subtitle: 'ASA, SOAP, OAA, ASRA', url: BB_DOC('_329349_1') },
      ] },
    ],
  },

  guides: {
    pph: {
      title: 'Postpartum Hemorrhage',
      intro: 'Harris Health obstetric hemorrhage checklist, October 2019. Complete every earlier stage regardless of where the patient presents. Repeat each verbal order back.',
      sections: [
        { title: 'Recognise', blocks: [{ type: 'bullets', items: [
          'Call the charge nurse and the OB attending',
          'Designate a team lead, a checklist reader, a second RN',
          'Announce cumulative blood loss and vitals',
          'Anesthesia is notified at 1000 mL by the labor nurse',
        ] }] },
        { title: 'Stage 1 · 500–1000 mL', blocks: [{ type: 'bullets', items: [
          '16 or 18 g IV, empty the bladder, fundal massage, vitals q5 min',
          'Oxytocin at bolus rate, cumulative maximum 40 units',
          'PPH kit to the bedside, QBL q15 min, find the cause, consider balloon tamponade',
          'Early TXA 1 g for coagulopathy, abruption or retained products',
        ] }] },
        { title: 'Stage 2 · 1000–1500 mL', blocks: [{ type: 'bullets', items: [
          'Second large-bore IV, prepare the OR if indicated',
          'Stat CBC, PT/PTT, INR, fibrinogen. Type and cross 2 units',
          'Transfuse on clinical signs. Do not wait for labs',
        ] }] },
        { title: 'Stage 3 · over 1500 mL or unstable', blocks: [{ type: 'bullets', items: [
          'Activate OB Emergency. Move to the OR and state the plan',
          'TXA 1 g IV over 10 minutes. Second gram if bleeding persists after 30 minutes',
          'Re-dose antibiotics',
          'Activate MTP and say "obstetric patient"',
          'Stat labs q30 min: CBC, coags, fibrinogen, gas, electrolytes with ionised calcium. TEG or ROTEM',
          'Warm all products. Core temperature and active warming',
          'Anesthesia attending: tech, rapid transfuser, blood warmer, EV1000, A-line, central line, goal-directed transfusion',
        ] }, { type: 'note', tone: 'danger', text: 'Over 2000 mL or suspected DIC: GYN or GYN-ONC backup, TSICU bed, IR embolization, uterine artery ligation or hysterectomy. Refractory: rFVIIa 40–60 mcg/kg, RiaSTAP 900–1300 mg.' }] },
        { title: 'Uterotonics', blocks: [{ type: 'rows', rows: [
          { label: 'Oxytocin', value: '30 U in 500 mL', note: '167 mL = 10 U. Bolus rate to 40 U total' },
          { label: 'Methylergonovine', value: '0.2 mg IM', note: 'q2–4 h. Avoid in hypertension' },
          { label: 'Carboprost', value: '250 mcg IM', note: 'q15 min, max 8. Avoid in asthma' },
          { label: 'Misoprostol', value: '800 mcg PR or 600 buccal', note: 'Max 1000 mcg. Little benefit after oxytocin' },
          { label: 'TXA', value: '1 g in 100 mL over 10–20 min', note: 'Filter needle to draw. Fast push drops the BP' },
        ] }] },
        { title: 'Procedures by cause', blocks: [{ type: 'bullets', items: [
          'Trauma: visualise and repair. Retained placenta: D&C',
          'Atony or lower segment bleeding: intrauterine balloon, then selective embolization',
          'At cesarean: B-Lynch suture, balloon',
          'Uterine inversion: nitroglycerin 2 sprays sublingual to relax the uterus for manual reduction',
        ] }] },
      ],
      sources: [
        { title: 'Obstetric Hemorrhage Checklist', detail: 'Harris Health, 2019', href: BB_DOC('_257828_1') },
      ],
    },

    neuraxial: {
      title: 'Neuraxial Dosing',
      intro: 'Pump solutions: 0.1% ropivacaine with fentanyl 2 mcg/mL, or 0.125% bupivacaine with fentanyl 2 mcg/mL. Always bolus in small aliquots.',
      sections: [
        { title: 'Labor epidural', confirm: true, blocks: [{ type: 'rows', rows: [
          { label: 'Initiation', value: '3–5 mL test dose + 5–10 mL pump solution' },
          { label: 'Infusion', value: '8–12 mL/h', note: 'Titrate to level. PCEA 3–5 mL q15–20 min. Pump max 30 mL/h' },
          { label: 'Top up', value: '5 mL pump solution', note: 'Then rate up 2 mL/h. Denser solution for breakthrough pain' },
          { label: 'Catheter', value: '17 g Tuohy, 19 g closed-end multi-orifice' },
        ] }] },
        { title: 'Labor CSE', blocks: [{ type: 'rows', rows: [
          { label: 'Intrathecal', value: '1–2 mL pump solution' },
          { label: 'Infusion', value: '1–2 mL/h pump solution', note: 'No PCEA' },
          { label: 'Epidural activation', value: 'No test dose, start the infusion' },
        ] }] },
        { title: 'Cesarean · spinal or CSE', blocks: [{ type: 'rows', rows: [
          { label: 'Hyperbaric bupivacaine 0.75%', value: '1.4–1.6 mL' },
          { label: 'Fentanyl', value: '10 mcg' },
          { label: 'Epinephrine', value: '100 mcg' },
          { label: 'PF morphine', value: '150 mcg' },
          { label: 'Block for incision', value: 'T4 with lower-extremity motor block' },
        ] }] },
        { title: 'Cesarean · existing labor epidural', blocks: [{ type: 'rows', rows: [
          { label: 'STAT', value: '3% chloroprocaine 3–5 mL q3 min' },
          { label: 'Urgent', value: '2% lidocaine with epi, bicarb, fentanyl, 3–5 mL q3 min', note: '10–15 mL total for a T4 level' },
          { label: 'After delivery', value: 'Duramorph 3 mg + butorphanol 1 mg epidural', note: 'Not if intrathecal Duramorph was given' },
        ] }, { type: 'note', tone: 'warn', text: 'Decision to delivery within 30 minutes: start bolusing and get the patient to the OR immediately.' }] },
        { title: 'Postpartum tubal ligation', blocks: [{ type: 'rows', rows: [
          { label: 'Spinal', value: 'Hyperbaric bupivacaine 0.75% 1.4–1.6 mL + fentanyl 10 mcg', note: 'No Duramorph' },
          { label: 'Existing epidural', value: '2% lidocaine with epi and bicarb, 10–15 mL for T4' },
        ] }] },
        { title: 'Cerclage, ECV', blocks: [{ type: 'rows', rows: [
          { label: 'Cerclage', value: 'Spinal: hyperbaric bupivacaine 0.75% + fentanyl 10 mcg', note: 'No Duramorph. Or CSE with 1–2 mL pump solution intrathecally' },
          { label: 'External cephalic version', value: 'Epidural or CSE', note: 'Check with faculty' },
        ] }] },
        { title: 'Contraindications', blocks: [{ type: 'bullets', items: [
          'Refusal or inability to cooperate, consent or position',
          'Raised intracranial pressure from a mass lesion. Pseudotumor cerebri is not a contraindication',
          'Infection at the needle site',
          'Coagulopathy or significant thrombocytopenia',
          'Uncorrected hypovolemia',
        ] }] },
      ],
      sources: [{ title: 'Trainee primer: clinical', detail: 'Blackboard, 2021', href: BB_DOC('_268622_1') }],
    },

    cesarean: {
      title: 'Cesarean',
      sections: [
        { title: 'Urgency classes', blocks: [{ type: 'rows', rows: [
          { label: 'STAT', value: 'Immediate threat to life', note: 'Rupture, cord prolapse, Category III, uncontrolled PPH, perimortem. Abbreviated timeout, no pre-op count' },
          { label: 'Within 30 minutes', value: 'Concern, not immediately life-threatening', note: 'Breech in advanced labor, active herpes, some Category II. With an epidural: bolus and move now' },
          { label: 'Within 3 hours', value: 'Unscheduled but indicated', note: 'Arrest of dilation or descent, breech in early labor' },
          { label: 'Elective', value: 'Scheduled or routine add-on', note: 'No time interval. Always ask for clarification' },
        ] }, { type: 'note', text: 'Two or more cesareans posted: nursing, OB and anesthesia huddle at the front desk to prioritise.' }] },
        { title: 'OR setup', blocks: [{ type: 'bullets', items: [
          'Every OB OR is set up and ready at all times, including right after a case',
          'Machine, monitors, suction, airway equipment, pressure bags, Alaris pumps',
          'Fridge: 50 mL phenylephrine syringe, epi and norepi sticks, oxytocin 30 U/500 mL premixed, carboprost 250 mcg, methylergonovine 250 mcg',
          'Pyxis: oxytocin 10 U/mL vials, misoprostol 6–8 tabs in drawer 1–2',
        ] }] },
        { title: 'Before incision', blocks: [{ type: 'bullets', items: [
          'Current CBC and BMP, type and screen. Cross as needed. Two IVs with extensions',
          'Bicitra 30 mL, famotidine 20 mg IV an hour before, metoclopramide case by case',
          'Hemorrhage risk low, moderate or high. Blood in the room to match',
          'OB and anesthesia attendings at the timeout before neuraxial',
          'Left uterine displacement. T4 level confirmed before incision',
          'Cefazolin 2 g, 3 g over 120 kg. Intrapartum: add azithromycin 500 mg. PCN allergy: clindamycin 900 mg + gentamicin 1.5 mg/kg',
        ] }] },
        { title: 'After delivery', blocks: [{ type: 'bullets', items: [
          'Oxytocin after cord clamp. See Uterotonics',
          'Ondansetron 4 mg. Dexamethasone 4 mg if more is needed',
          'Ketorolac 30 mg IV at the end if allowed',
          'No neuraxial morphine: bilateral TAP blocks ropivacaine 0.2% 20 mL per side, or wound infiltration',
          'Debrief with QBL and pressor use. Remove the epidural catheter unless coagulopathy is suspected or it is staying for PCEA',
        ] }] },
      ],
      sources: [
        { title: 'Cesarean decision-to-surgery timing', detail: 'Harris Health guideline E-14', href: BB_DOC('_257828_1') },
        { title: 'Trainee primer: clinical', detail: 'Blackboard, 2021', href: BB_DOC('_268622_1') },
      ],
    },

    phenylephrine: {
      title: 'Phenylephrine Infusion',
      intro: 'For spinal hypotension at cesarean. Ben Taub OB Anesthesia, September 2020.',
      sections: [
        { title: 'Prepare', blocks: [{ type: 'steps', items: [
          '50 mL of 100 mcg/mL phenylephrine from pharmacy in a 60 mL BD syringe, small-bore extension primed',
          'Alaris pump set to 40 mcg/min, range 30–50',
          '1 L crystalloid on a pressure bag for the co-load',
        ] }] },
        { title: 'Before the spinal', blocks: [{ type: 'steps', items: [
          'Note the baseline systolic pressure',
          'Infusion onto the most proximal IV port',
          'NIBP cycling every 3 minutes',
          'Start the 1 L co-load',
        ] }] },
        { title: 'After the spinal', blocks: [{ type: 'steps', items: [
          'Start the infusion as the injection ends. NIBP every minute',
          'Keep systolic at or above 90% of baseline, titrating 10–20 mcg/min at a time',
          'Reflex bradycardia: glycopyrrolate or ephedrine',
        ] }] },
        { title: 'After delivery', blocks: [{ type: 'bullets', items: [
          'Wean. Off by the time the hysterotomy is closed',
          'Mild asymptomatic hypotension is acceptable',
          'Nausea or vomiting with hypotension while weaning: look for hidden blood loss',
          'Still needed at fascial closure: tell the upper level or attending',
        ] }, { type: 'note', text: 'Heart rate is the best surrogate for cardiac output. Pre-eclamptic patients need less.' }] },
      ],
      sources: [{ title: 'Phenylephrine protocol', detail: 'Blackboard', href: BB_DOC('_257828_1') }],
    },

    erac: {
      title: 'Enhanced Recovery',
      intro: 'Enhanced recovery after cesarean. All scheduled cesareans since February 2021. Intrapartum cesareans can follow the same post-op orders. Not for urgent cases under general anesthesia.',
      segments: [
        { id: 'before', label: 'Before', sections: [
          { title: 'Antepartum clinic', items: [
            'Pre-op anesthesia consult in PACT for every scheduled cesarean, earlier if complicated',
            'Tick ERAS protocol in the pre-op evaluation and add an ERAC comment',
            'Anemia: oral iron, assess for IV iron. CBC, type and screen',
          ] },
          { title: '72 hours before', items: [
            'Pre-op evaluation and e-consent: general, spinal, epidural, TAP or QL blocks, perinatal complications',
            'Explain the post-op regimen: scheduled alternating acetaminophen and ketorolac, then ibuprofen',
            'CBC and type and screen, valid 72 hours. COVID test when required',
            'Two clear Ensure from PACT: one at 9 pm the night before, one finished 2 hours before arrival. Not for diabetics, who can still be ERAC',
            'Clears until 2 hours before, no solids for 8. Hibiclens the night before and the morning of',
            'No pre-op oral tramadol or gabapentin',
          ] },
        ] },
        { id: 'dos', label: 'Day of surgery', sections: [
          { title: 'Pre-op', items: [
            'Review H&P, NPO, allergies, labs. Confirm type and screen or crossmatch',
            'Two IVs with extensions. Bicitra 30 mL. Famotidine 20 mg IV an hour before. Metoclopramide case by case',
            'State the hemorrhage risk. Blood in the room to match. Order products if needed',
          ] },
          { title: 'Intra-op', items: [
            'Crystalloid co-load during the spinal, 10–15 mL/kg IBW during the case, under 3 L for an uncomplicated case',
            'Fluid warmer on one IV if hemorrhage risk is moderate or high. Foley with temperature probe. Remind the nurse at timeout',
            'OR at 72–74 °F. Warm fluids and forced-air warming. Skin temperature above 36',
            'Phenylephrine infusion from 30–35 mcg/min right after the spinal, titrate to heart rate, off as soon as stable',
            'Spinal: hyperbaric bupivacaine 10–12 mg, PF morphine 150 mcg, epinephrine 100 mcg, fentanyl 10–15 mcg',
            'Comfortable under neuraxial: no unnecessary opioids',
            'Ondansetron 4 mg after delivery, dexamethasone 4 mg if needed',
            'Oxytocin after cord clamp, then methylergonovine, carboprost or misoprostol for poor tone',
            'Ketorolac 30 mg at the end if allowed. No PF morphine: TAP blocks or wound infiltration',
            'Delayed cord clamping 30–60 s unless mother or baby is unstable',
          ] },
        ] },
        { id: 'after', label: 'After', sections: [
          { title: 'PACU', items: [
            'Duramorph orders and the APMS follow-up list',
            'Scheduled alternating IV acetaminophen 1 g and ketorolac 30 mg, each q6h for 24 hours, at specific times so pharmacy does not align them',
            'Clears in PACU, solids within 4 hours',
            'Foley out at 12 hours unless on magnesium or after hemorrhage. Bladder scan if no void by 6 hours',
            'Incentive spirometry, ambulation, senior signout before the floor',
          ] },
          { title: 'Floor', items: [
            'POD0: Duramorph order set. Bowel regimen. IV PCA only without neuraxial morphine, stopped by noon POD1',
            'After 24 hours: OB manages with scheduled oral acetaminophen and ibuprofen, Norco 5 mg prn only. Consult APMS for poor control',
            'POD1: assess for PDPH, nerve injury, urinary retention',
            'Discharge POD3 by noon. Notify anesthesia of headache, back pain or neurologic symptoms before discharge',
          ] },
        ] },
      ],
      sources: [{ title: 'ERAC protocol', detail: 'Blackboard', href: BB_DOC('_257828_1') }],
    },

    duramorph: {
      title: 'Duramorph Orders',
      intro: 'Preservative-free neuraxial morphine. Cesarean patients only, or third- and fourth-degree tears after vaginal delivery with faculty approval.',
      sections: [
        { title: 'Dose', blocks: [{ type: 'rows', rows: [
          { label: 'Intrathecal', value: '150 mcg' },
          { label: 'Epidural', value: '3 mg', note: 'No butorphanol with it' },
        ] }] },
        { title: 'Orders', blocks: [{ type: 'bullets', items: [
          'Every Duramorph patient goes on the Duramorph follow-up list, not the OB list. APMS sees them',
          'Complete every tab of the Duramorph order set: antihistamine for pruritus, scheduled IV acetaminophen and ketorolac for 24 hours',
          'Chart the time Duramorph was given. The order set runs 24 hours from then',
          'Tell OB not to write scheduled acetaminophen or ibuprofen in the first 24 hours. Check the MAR first',
        ] }] },
        { title: 'Follow-up', blocks: [{ type: 'bullets', items: [
          'Karla Maldonado, APMS, rounds on Duramorph patients',
          'Without her: the OB team leader on weekdays, the APMS moonlighter on weekends and holidays. The attending cosigns and bills if the leader is a resident',
        ] }] },
      ],
      sources: [{ title: 'Refresher on orders', detail: 'Blackboard', href: BB_DOC('_268622_1') }],
    },

    eclampsia: {
      title: 'Eclamptic Seizure',
      intro: 'Magnesium sulfate bolus for a seizure in the operating room. Doses are decided by the OB faculty; anesthesia gives them.',
      sections: [
        { title: 'First', blocks: [{ type: 'steps', items: [
          'Announce the seizure to OB and nursing. Both faculties to the OR',
          'Circulation, airway, breathing. Delegate starting magnesium to OB or nursing if you are on the airway',
          'Assign a runner for the right bag and tell them where it lives',
          'Give the bolus over 20 minutes on an Alaris pump. Standard tubing, compatible with the usual anesthetic drugs',
        ] }] },
        { title: 'Dose', blocks: [{ type: 'rows', rows: [
          { label: 'Already on an infusion', value: '2 g IV over 20 min', note: '2 g in 50 mL. 3F OR central Pyxis' },
          { label: 'Not yet loaded', value: '6 g IV over 20 min, then 2 g/h', note: '6 g in 100 mL. 3F OR central Pyxis' },
          { label: 'No IV access', value: '10 g IM, 5 g each buttock', note: '50% syringes, 3F nursing station Pyxis. Or IO 6 g in 100 mL' },
          { label: 'Renal insufficiency', value: '4 g load + 1 g/h, or 4–6 g load alone', note: 'Cr over 1.1 or UOP under 30 mL/h for 4 h. Level q4–6 h' },
          { label: 'Second line', value: 'Midazolam 2 mg' },
        ] }] },
        { title: 'Levels', blocks: [{ type: 'rows', rows: [
          { label: 'Therapeutic', value: '5–9 mg/dL' },
          { label: 'Loss of reflexes', value: '9.6–12 mg/dL' },
          { label: 'Respiratory depression', value: '12–18 mg/dL' },
          { label: 'Cardiac arrest', value: '24–30 mg/dL' },
        ] }, { type: 'bullets', items: ['Check a level after a breakthrough seizure, in renal insufficiency, or with signs of toxicity'] }] },
        { title: 'Toxicity', blocks: [{ type: 'bullets', items: [
          'Stop the infusion and tell OB',
          'Severe cardiopulmonary compromise: calcium gluconate 10% 1 g IV over 2–5 min, up to 3 g in arrest. Or calcium chloride 500–1000 mg',
          'Fluids, furosemide, magnesium level',
          'Contraindicated in myasthenia gravis and hypocalcemia',
        ] }, { type: 'note', tone: 'warn', text: 'Magnesium worsens atony. Have blood available if there is hemorrhage risk, and consider calcium for atony that does not respond to uterotonics.' }] },
      ],
      sources: [{ title: 'MgSO4 for eclamptic seizure in the OR', detail: 'Blackboard', href: BB_DOC('_257828_1') }],
    },

    sepsis: {
      title: 'Sepsis',
      intro: 'Suspected infection with oral temperature under 36 or over 38 °C.',
      sections: [
        { title: 'Screen · positive if any', blocks: [{ type: 'bullets', items: [
          'SBP under 90 or at least 160, DBP at least 110',
          'HR under 50 or over 120',
          'RR under 10 or over 30',
          'SpO2 under 95%',
        ] }] },
        { title: 'Within one hour', blocks: [{ type: 'bullets', items: [
          'Empiric source-directed antibiotics, ordered STAT with a call to pharmacy',
          '1–2 L IV fluid',
          'CBC, CMP, PT/INR, PTT, lactate, blood and urine cultures. Mental status',
          'MAP under 65: septic shock. Cultures, chest film, MFM and MICU consults, vasopressors',
        ] }] },
        { title: 'Antibiotics', blocks: [{ type: 'rows', rows: [
          { label: 'Uterine', value: 'Ampicillin 2 g q6h + gentamicin 5 mg/kg q24h + clindamycin 900 mg q8h' },
          { label: 'Pyelonephritis', value: 'Ceftriaxone 2 g q24h', note: 'Meropenem and vancomycin if ESBL risk, fever over 48 h, pressors or deterioration' },
          { label: 'Unknown, urinary, abdomen', value: 'Meropenem 1 g q8h + vancomycin 25 mg/kg q24h' },
          { label: 'Skin and soft tissue', value: 'Meropenem + vancomycin + clindamycin 900 mg q8h' },
          { label: 'Pneumonia', value: 'Ceftriaxone 2 g q24h + azithromycin 500 mg daily' },
          { label: 'Meningitis', value: 'Ceftriaxone 2 g q12h + vancomycin 20 mg/kg q12h + ampicillin 2 g q6h' },
        ] }] },
        { title: 'Confirm · one or more', blocks: [{ type: 'bullets', items: [
          'Ventilation or PaO2/FiO2 under 300',
          'Platelets under 100, INR over 1.5 or PTT over 60',
          'Bilirubin over 2',
          'SBP under 85, MAP under 65, or a 40 mmHg drop after fluids',
          'Creatinine 1.2 or doubled, or urine under 35 mL/h for 2 hours',
          'Altered mental status',
          'Lactate over 2. In labor: recheck in 2 hours',
        ] }, { type: 'bullets', items: ['Confirmed: MFM consult, transfer to L&D, fluids to 30 mL/kg, imaging, lactate q2h'] }] },
      ],
      sources: [{ title: 'OB sepsis algorithm', detail: 'Blackboard', href: BB_DOC('_257828_1') }],
    },

    drugs: {
      title: 'OB Drugs',
      sections: [
        { title: 'Look-alikes', blocks: [{ type: 'note', tone: 'danger', text: 'Pitocin, phenylephrine, TXA and ephedrine come in similar vials. TXA and hyperbaric bupivacaine ampoules look alike, and neuraxial TXA is catastrophic. Check the drug, route and dose every time, and check the IV line before injecting.' }] },
        { title: 'Uterotonics', blocks: [{ type: 'rows', rows: [
          { label: 'Oxytocin', value: 'Per protocol', note: 'Alaris: Critical Care, Anesthesia mode, OxytocinAnes' },
          { label: 'Methylergonovine', value: '0.2 mg IM only', note: 'q2–4 h. OR fridge. Avoid hypertension and pre-eclampsia' },
          { label: 'Carboprost', value: '250 mcg IM only', note: 'q15 min, max 8. OR fridge. Avoid asthma' },
          { label: 'Misoprostol', value: '800 mcg SL q3h × 2', note: 'Pyxis. Nausea, tachysystole' },
        ] }] },
        { title: 'Tocolytics', blocks: [{ type: 'rows', rows: [
          { label: 'Magnesium', value: '4–6 g load, 1–2 g/h', note: 'Nurse-given. Atony risk, hypotension. Toxicity: calcium gluconate 1 g' },
          { label: 'Terbutaline', value: '0.25 mg IV or SC q20 min × 3', note: 'Tachycardia, pulmonary edema, atony risk' },
          { label: 'Nitroglycerin', value: 'SL 400 mcg q5, or IV 50 mcg to 200', note: 'Anesthesia may give. Headache, hypotension' },
        ] }] },
        { title: 'Local anesthetics', blocks: [{ type: 'rows', rows: [
          { label: 'Bupivacaine 0.75% hyperbaric', value: '0.8–1.6 mL intrathecal' },
          { label: 'Bupivacaine 0.125% or ropivacaine 0.1%', value: '8–12 mL/h epidural', note: '3–5 mL increments. 1 mL bicarb 8.4% per 10 mL' },
          { label: 'Lidocaine 2%', value: '3–5 mL increments', note: 'With epi 1:200k and bicarb' },
          { label: 'Chloroprocaine 3%', value: '15–25 mL in increments', note: 'Repeat q30–40 min. Bicarb does nothing. Caution with cholinesterase disorders' },
        ] }] },
      ],
      sources: [{ title: 'Look-alike drugs on OB anesthesiology', detail: 'Blackboard', href: BB_DOC('_268622_1') }],
    },

    epic: {
      title: 'Epic',
      segments: [
        { id: 'epidural', label: 'Labor epidural', sections: [
          { title: 'Consent', items: [
            'Electronic. Get the nurse\'s ID number first or the consent is lost',
            'Planned procedure: "Labor, possible CS, possible ppBTL and all other indicated procedures"',
            'Consent for general, spinal and epidural, regional, and prenatal or early childhood complications. Spanish consent with an interpreter',
          ] },
          { title: 'Charting the placement', blocks: [{ type: 'steps', items: [
            'Intraprocedure, then Anesthesia Start',
            'Start Out-of-OR vitals. Timeout',
            'Place the neuraxial. Bolus and record vitals for at least 15 minutes',
            'Meds: pick the epidural infusion under OB meds twice, once in mL for boluses and once in mL/h for the infusion',
            'Procedure note with the test dose. 17 g Tuohy, 19 g closed-end multi-orifice catheter',
            'Stop Out-of-OR vitals',
          ] }] },
          { title: 'Every bolus', items: ['Chart the meds and a Q note: insertion site, comfort with contractions, sensory level, knee bend, fetal heart rate'] },
          { title: 'Grease board', items: ['The Notes column is anesthesia-only and survives readmission. Date every note'] },
        ] },
        { id: 'cassettes', label: 'Cassettes', sections: [
          { title: 'Replacing', items: [
            'Chart totals must match the cassettes pulled from the Pyxis: 3 cassettes means 300 mL documented, infusion plus boluses',
            'Add PCEA boluses by hand. The pumps do not talk to Epic',
            'After the swap, choose New Bag under the running infusion and accept the same-medication alert',
          ] },
          { title: 'Wasting', items: [
            'Aspirate the contents with 20 mL syringes into the cactus bucket at the LDR front desk',
            'Pyxis waste plus chart must equal the total issued',
            'Empty cassettes go in the black bucket in the biohazard room. Remove patient labels',
          ] },
        ] },
        { id: 'cesarean', label: 'To cesarean', sections: [
          { title: 'Merge the encounters', blocks: [{ type: 'steps', items: [
            'The OB resident places a case request and nursing drags it to the assigned OR. Nothing works until they do',
            'On the OR board, double-click the posting and accept the prompt to merge',
            'Use the Labor to CS macro, which includes Transport to OR',
          ] }] },
          { title: 'Catheter removal', items: [
            'Remove after every cesarean and vaginal delivery, unless coagulopathy is suspected or it stays for PCEA',
            'Click Epidural Removed, then document reason, assessment and time under Lines',
          ] },
          { title: 'Closing', items: [
            'Complete all three Post-evaluation tabs',
            'Follow-up list: OB for labor epidurals, Duramorph for cesareans that got it',
            'Close Encounter and sign. The senior closes OR cases, the junior may close vaginal deliveries',
          ] },
        ] },
      ],
      sources: [{ title: 'Trainee primer: logistics', detail: 'Blackboard, 2021', href: BB_DOC('_268622_1') }],
    },

    daily: {
      title: 'Daily Checks & Schedule',
      segments: [
        { id: 'checks', label: 'Checks', intro: 'Done before signout by whoever is on the daily assignment schedule posted in the hallway. Report anything missing to the senior or attending.', sections: [
          { title: 'Each LDR cabinet', items: ['Phenylephrine and ephedrine, one each', 'Two cesarean tackle boxes', 'Syringes, IV start kits, blunt-tip syringes for sensory levels', 'Working suction with a Yankauer'] },
          { title: 'Each OR', items: ['ETT 6.0 and 7.0, laryngoscope with Mac and Miller blades, bougie, stylet, oral airways 8 to 10, i-gel 3 to 5, suction', 'Monitors, pressure bags, Alaris pumps'] },
          { title: 'OR fridge', items: ['60 mL phenylephrine syringe, epi and norepi sticks', 'Oxytocin 30 U/500 mL premixed, carboprost 250 mcg, methylergonovine 250 mcg', 'Oxytocin 10 U/mL vials and misoprostol, 6 to 8 tabs, in the Pyxis'] },
          { title: 'OG 10 and the workroom', items: ['Fiberoptic bronchoscope on the airway tower', 'C-MAC with #3, #4 and D-blade, GlideScope', 'McGrath in the workroom drawer, blades in the locked workroom'] },
        ] },
        { id: 'schedule', label: 'Schedule', sections: [
          { title: 'Signout', blocks: [{ type: 'rows', rows: [
            { label: 'Weekdays', value: '07:00 and 15:30' },
            { label: 'Weekends and holidays', value: '08:00', note: 'In the LRC. Arrive 07:30 for equipment checks' },
            { label: 'Split shifts', value: '18:00', note: 'Arrive 17:30' },
            { label: 'L&D huddle', value: '08:00 and 20:30', note: 'LD nursing station. 08:30 on weekends' },
          ] }, { type: 'bullets', items: ['Arrive 30 minutes before signout. Late or absent: call the LD attending and email the site scheduler'] }] },
          { title: 'Assignment codes', blocks: [{ type: 'rows', rows: [
            { label: 'LD', value: 'Labor deck', note: '06:30 until dismissed after rounds' },
            { label: 'LO', value: 'Labor electives', note: '06:30. Pre-ops for scheduled cases, then the deck. Call the LO attending the night before' },
            { label: 'LS', value: 'Late start', note: '10:00, last to leave' },
            { label: 'C', value: 'Weekday call', note: '15:00 until after morning rounds' },
            { label: 'W', value: 'Weekend or holiday 24 h', note: '07:30 to after rounds' },
            { label: 'AM / PM', value: 'Split call', note: '07:30–18:00 and 18:00 to after rounds' },
            { label: 'O', value: 'Orientation', note: 'First weekday, 06:30' },
          ] }, { type: 'bullets', items: ['Dr Tzan makes the call schedule. Requests by the 10th of the preceding month'] }] },
          { title: 'Team phones', items: ['Every member of the call team carries a team phone at all times', 'Batteries only from the anesthesia charging station in the LRC', 'One trainee never holds both junior phones'] },
        ] },
      ],
      sources: [{ title: 'OB orientation slides', detail: 'Blackboard, February 2022', href: BB_DOC('_268622_1') }],
    },

    roles: {
      title: 'Team Roles',
      intro: 'PGY2 and PGY3 are team members, PGY3 and PGY4 lead. Everyone prepares every patient for signout rounds.',
      sections: [
        { title: 'CA-1', items: [
          'Check the ORs and LDRs. ORs are set up at all times, including right after a case',
          'Evaluate every new L&D admission: pre-evaluation, labs, consent, extra IVs. Consent only with a nurse present',
          'Present new patients to the team leader or attending',
          'Place epidurals with supervision. Follow them every 2 hours and update the labor assessment with vitals',
          'Two failed troubleshooting attempts: call the team leader or attending',
          'Complete charting: pre-op, consent, intra-op, narcotic reconciliation, post-op notes and orders',
        ] },
        { title: 'CA-2', items: ['Everything above', 'Lead some days to prepare for the role', 'When leading, place at least one neuraxial per shift rather than only supervising'] },
        { title: 'CA-3, team leader', items: [
          'Owns the high-risk patients and knows everyone on the unit',
          'Gloved and present for every neuraxial. Does one or two per shift and the complicated ones',
          'Present for all urgent and STAT cases and complicated vaginal deliveries',
          'Urgent cesareans in the OR for delivery within 30 minutes of the decision',
          'Tells the attending before every epidural and about any troubleshooting',
          'Ensures a clinical debrief after every case',
          'Signs SRNA consents and notes when no CRNA is available. Places GI prophylaxis and APMS orders with SRNAs',
          'Signs patients out of PACU and the LDRs. Rounds on post-epidural patients and reports PDPH or neurologic complications',
          'Carries the APMS pager on weeknights: rounds on catheter patients around 21:00 with a nightly note',
        ] },
      ],
      sources: [{ title: 'Rotation resident responsibilities', detail: 'Blackboard, September 2021', href: BB_DOC('_268622_1') }],
    },
  },
};
